import { gql } from "@apollo/client";
import * as Sentry from "@sentry/react";

import AmplitudeService from "src/services/amplitude";
import { useCreateQueryScheduleMutation } from "src/gql/codegen/graphql";
import { isNonNullable, isNullable } from "src/utils/types/types";
import { getRefetchQueryScheduleOptions } from ".";

import type { SessionWithUser } from "src/utils/users/types";
import type { FormState } from "shared/ScheduleExecutionModalContent";
import { toRateOption } from "shared/ScheduleExecutionModalContent/cron/optionsRate";

export function useCreateQuerySchedule() {
  const [callCreateQueryScheduleMutation, createQueryScheduleResult] =
    useCreateQueryScheduleMutation();

  function createQuerySchedule(
    session: SessionWithUser,
    query_id: number,
    form: FormState
  ): Promise<string> {
    if (isNullable(session) || createQueryScheduleResult.loading) {
      return new Promise(() => {});
    }

    return new Promise((resolve) => {
      callCreateQueryScheduleMutation({
        context: { session },
        variables: {
          query_id,
          cron_expression: form.cronString,
          performance: form.performanceTier,
        },
        refetchQueries: [getRefetchQueryScheduleOptions(session, query_id)],
      }).then(
        ({ data }) => {
          AmplitudeService.trackEvent("Query Schedule Created", {
            query_id: query_id,
            interval: toRateOption(form.cronString) ?? form.cronString,
            performance: form.performanceTier,
          });
          if (isNonNullable(data)) {
            resolve(data.create_query_schedule.cron_job.id);
          } else {
            throw new Error("Unable to retrieve query schedule after creation");
          }
        },
        (error) => {
          Sentry.captureException(
            "Failed to create query schedule: " + error.message
          );
        }
      );
    });
  }

  return [createQuerySchedule, createQueryScheduleResult] as const;
}

gql`
  mutation CreateQuerySchedule(
    $query_id: Int!
    $cron_expression: String!
    $performance: String!
  ) {
    create_query_schedule(
      query_id: $query_id
      cron_expression: $cron_expression
      performance: $performance
    ) {
      query_id
      cron_job {
        id
      }
    }
  }
`;
